import { LoanParamsService } from "@/services/network/loan-params";
import { DealerMasterParams } from "@/services/network/type";
import { useInfiniteQuery } from "@tanstack/react-query";

const loanParamsService = new LoanParamsService();

interface Dealer {
  dealerCode: string;
  dealerName: string;
}

interface DealerMasterResponse {
  total: number;
  page: number;
  totalPages: number;
  data: Dealer[];
}

interface ApiResponse {
  status: number;
  message: string;
  data: DealerMasterResponse;
}

const useDealerMaster = (params: DealerMasterParams) => {
  const {
    data,
    error,
    isError,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery<ApiResponse, Error>({
    queryKey: [LoanParamsService.QUERY_KEY.DEALER_MASTER, params],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await loanParamsService.getDealerMaster({
        ...params,
        page: pageParam as number,
      });
      if (response.status === 200 && response.data) {
        return response;
      } else {
        throw new Error(response.message || "Failed to fetch dealers");
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.page < lastPage.data.totalPages) {
        return lastPage.data.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: false,
  });

  const transformedData = data?.pages.flatMap((page) => page.data.data);

  return {
    data: transformedData,
    error,
    isError,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  };
};

export { useDealerMaster };

export type { Dealer };
